import React, { useState } from 'react';
import { CONTACT, COLORS } from '../../data/siteData';
import { Check, Loader2 } from 'lucide-react';

const ContactForm = ({ isModal = false, onSuccess }) => {
  const [formData, setFormData] = useState({
    organization: '',
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit');
      }

      setIsSuccess(true);
      setFormData({
        organization: '',
        name: '',
        email: '',
        phone: '',
        message: '',
      });

      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
        }, 2000);
      }
    } catch (err) {
      setError(CONTACT.form.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess && !isModal) {
    return (
      <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
        <div 
          className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
          style={{ backgroundColor: COLORS.green + '20' }}
        >
          <Check className="w-8 h-8" style={{ color: COLORS.green }} />
        </div>
        <p className="text-lg font-medium" style={{ color: COLORS.dark }}>
          {CONTACT.form.success}
        </p>
      </div>
    );
  }

  const formContent = (
    <form onSubmit={handleSubmit} className="space-y-5">
      {isSuccess && isModal && (
        <div 
          className="p-4 rounded-lg mb-4 text-center"
          style={{ backgroundColor: COLORS.green + '15', color: COLORS.green }}
        >
          {CONTACT.form.success}
        </div>
      )}
      
      {error && (
        <div 
          className="p-4 rounded-lg mb-4 text-center"
          style={{ backgroundColor: COLORS.red + '15', color: COLORS.red }}
        >
          {error}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium mb-2" style={{ color: COLORS.dark }}>
          {CONTACT.form.organization} *
        </label>
        <input
          type="text"
          name="organization"
          value={formData.organization}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 transition-all"
          style={{ 
            '--tw-ring-color': COLORS.green,
          }}
          data-testid="contact-organization"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2" style={{ color: COLORS.dark }}>
          {CONTACT.form.name} *
        </label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 transition-all"
          data-testid="contact-name"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2" style={{ color: COLORS.dark }}>
          {CONTACT.form.email} *
        </label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 transition-all"
          data-testid="contact-email"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2" style={{ color: COLORS.dark }}>
          {CONTACT.form.phone}
        </label>
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 transition-all"
          data-testid="contact-phone"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2" style={{ color: COLORS.dark }}>
          {CONTACT.form.message} *
        </label>
        <textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          rows={4}
          className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 transition-all resize-none"
          data-testid="contact-message"
        />
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full py-3 px-6 text-white font-medium rounded-lg transition-all hover:opacity-90 disabled:opacity-50 flex items-center justify-center"
        style={{ backgroundColor: COLORS.green }}
        data-testid="contact-submit"
      >
        {isSubmitting ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          CONTACT.form.submit
        )}
      </button>
    </form>
  );

  if (isModal) {
    return formContent;
  }

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: COLORS.dark }}>
            {CONTACT.title}
          </h2>
          <p className="text-lg" style={{ color: COLORS.gray }}>
            {CONTACT.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start max-w-5xl mx-auto">
          {/* Form */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
            {formContent}
          </div>

          {/* Benefits */}
          <div className="lg:pt-8">
            <h3 className="text-xl font-bold mb-6" style={{ color: COLORS.dark }}>
              {CONTACT.benefits.title}
            </h3>
            <ul className="space-y-4">
              {CONTACT.benefits.items.map((item, index) => (
                <li key={index} className="flex items-center space-x-3">
                  <div 
                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: COLORS.green }}
                  >
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <span style={{ color: COLORS.dark }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
